/**
 * JsonValue Backwards Compatibility Layer for Prisma 6.16.0
 * 
 * This file provides utilities to handle JsonValue type safety issues
 * introduced in Prisma 6.16.0 while maintaining backwards compatibility
 * with existing code that expects to access properties on JSON fields.
 * 
 * Note: Uses browser-compatible types instead of Prisma runtime imports
 * to avoid Node.js import issues in frontend bundlers.
 */

// Browser-safe type definitions that don't require Prisma runtime
export type JsonValue = string | number | boolean | null | JsonObject | JsonArray;
export type InputJsonValue = string | number | boolean | null | InputJsonObject | InputJsonArray;
export type JsonObject = { [Key in string]?: JsonValue };
export type JsonArray = JsonValue[];
export type InputJsonObject = { readonly [Key in string]?: InputJsonValue | null };
export type InputJsonArray = ReadonlyArray<InputJsonValue | null>;

import { PgAddress as CorePgAddress } from '@trashlab/api/src/domains/core/types';

// DocumentNode type for contract content
export interface DocumentNode {
  type: string;
  content?: DocumentNode[];
  attrs?: Record<string, unknown>;
  marks?: Array<{
    type: string;
    attrs?: Record<string, unknown>;
  }>;
  text?: string;
  kind?: string;
  definitions?: unknown[];
}

// Common types that are stored as JSON but accessed as typed objects
export interface PaymentMethodData {
  holder_name?: string;
  name?: string;
  brand?: string;
  last4?: string;
  last2?: string;
  [key: string]: unknown;
}

export interface ToolCall {
  [key: string]: unknown;
}

export interface LocationData {
  latitude?: number;
  longitude?: number;
  city?: string;
  state?: string;
  country?: string;
  [key: string]: unknown;
}

// DocumentNode type for contract content
export interface DocumentNode {
  type: string;
  content?: DocumentNode[];
  attrs?: Record<string, unknown>;
  marks?: Array<{
    type: string;
    attrs?: Record<string, unknown>;
  }>;
  text?: string;
}

// TipTap content interface  
export interface TipTapContent {
  type: string;
  content?: Array<any>;
  [key: string]: unknown;
}

// Type guards to safely check if JsonValue has expected properties
export function isJsonObject(value: JsonValue): value is JsonObject {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

export function isJsonArray(value: JsonValue): value is JsonArray {
  return Array.isArray(value);
}

export function hasProperty<T extends string>(
  obj: JsonValue,
  prop: T
): obj is JsonObject & Record<T, JsonValue> {
  return isJsonObject(obj) && prop in obj;
}

// Safe property access utilities
export function safeGetProperty<T = JsonValue>(
  obj: JsonValue | null | undefined,
  prop: string,
  defaultValue?: T
): T | undefined {
  if (!obj || !isJsonObject(obj) || !(prop in obj)) {
    return defaultValue;
  }
  return obj[prop] as T;
}

export function safeGetString(
  obj: JsonValue | null | undefined,
  prop: string,
  defaultValue: string = ''
): string {
  const value = safeGetProperty(obj, prop);
  return typeof value === 'string' ? value : defaultValue;
}

export function safeGetNumber(
  obj: JsonValue | null | undefined,
  prop: string,
  defaultValue: number = 0
): number {
  const value = safeGetProperty(obj, prop);
  return typeof value === 'number' ? value : defaultValue;
}

// Address utilities
export function asAddress(value: JsonValue | null | undefined): CorePgAddress {
  // Always return a valid address object with safe defaults
  const obj = value && isJsonObject(value) ? value : {};
  
  return {
    streetAddressPartOne: safeGetString(obj, 'streetAddressPartOne'),
    streetAddressPartTwo: safeGetString(obj, 'streetAddressPartTwo'),
    city: safeGetString(obj, 'city'),
    state: safeGetString(obj, 'state'),
    postalCode: safeGetString(obj, 'postalCode'),
    county: safeGetString(obj, 'county'),
    latitude: safeGetNumber(obj, 'latitude'),
    longitude: safeGetNumber(obj, 'longitude'),
  };
}

export function addressToJson(address: CorePgAddress | null | undefined): InputJsonValue {
  if (!address) {
    return {} as InputJsonValue;
  }
  
  return {
    streetAddressPartOne: address.streetAddressPartOne ?? null,
    streetAddressPartTwo: address.streetAddressPartTwo ?? null,
    city: address.city ?? null,
    state: address.state ?? null,
    postalCode: address.postalCode ?? null,
    county: address.county ?? null,
    latitude: address.latitude ?? null,
    longitude: address.longitude ?? null,
  };
}


// Location utilities (for fraud analysis, etc.)
export function asLocationData(value: JsonValue | null | undefined): LocationData {
  // Always return a valid LocationData object with safe defaults
  const obj = value && isJsonObject(value) ? value : {};
  
  return {
    latitude: safeGetNumber(obj, 'latitude'),
    longitude: safeGetNumber(obj, 'longitude'),
    city: safeGetString(obj, 'city'),
    state: safeGetString(obj, 'state'),
    country: safeGetString(obj, 'country'),
  };
}

  

// Payment method utilities with proper typing
export function asPaymentMethod(value: JsonValue | null | undefined): PaymentMethodData {
  // Always return a valid PaymentMethodData object with safe defaults
  const obj = value && isJsonObject(value) ? value : {};
  
  return {
    holder_name: safeGetString(obj, 'holder_name'),
    name: safeGetString(obj, 'name'),
    brand: safeGetString(obj, 'brand'),
    last4: safeGetString(obj, 'last4'),
    last2: safeGetString(obj, 'last2'),
  };
}

// Array utilities for complex objects
export function arrayToJson<T>(array: T[] | null | undefined): InputJsonValue {
  if (!array) {
    return [];
  }
  
  // Convert complex objects to plain objects for JSON storage
  return array.map(item => {
    if (typeof item === 'object' && item !== null) {
      return { ...item } as Record<string, unknown>;
    }
    return item;
  }) as InputJsonValue;
}

export function objectToJson<T extends Record<string, unknown>>(
  obj: T | null | undefined
): InputJsonValue {
  if (!obj) {
    return {} as InputJsonValue;
  }
  
  // Create a plain object copy for JSON storage
  return { ...obj } as InputJsonValue;
}

// Content utilities (for documents, AI chat, etc.)
export function asContentObject<T = JsonObject>(value: JsonValue | null | undefined): T {
  // Always return an object, defaulting to empty object if invalid
  if (!value || !isJsonObject(value)) {
    return {} as T;
  }
  
  return value as T;
}

// Safe casting utilities for common patterns
export function castToTyped<T>(value: JsonValue | null | undefined): T {
  // Cast to type T, with appropriate defaults
  if (value === null || value === undefined) {
    // Return appropriate default based on common types
    return {} as T;
  }
  
  return value as T;
}

// Validation utilities
export function isValidAddress(value: JsonValue | null | undefined): value is JsonObject {
  if (!value || !isJsonObject(value)) {
    return false;
  }
  
  // At minimum, should have some address components
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.streetAddressPartOne === 'string' ||
    typeof obj.city === 'string' ||
    typeof obj.state === 'string'
  );
}

// Migration helpers for updating JsonValue fields safely
export function migrateJsonField<T>(
  currentValue: JsonValue | null | undefined,
  transform: (obj: JsonObject) => T
): T {
  if (!currentValue || !isJsonObject(currentValue)) {
    // Try to transform empty object as fallback
    try {
      return transform({});
    } catch (error) {
      console.warn('Failed to migrate empty JSON field:', error);
      throw error; // Let caller handle the error
    }
  }
  
  try {
    return transform(currentValue);
  } catch (error) {
    console.warn('Failed to migrate JSON field:', error);
    // Try with empty object as last resort
    return transform({});
  }
}

// Type assertion helpers that maintain backwards compatibility
export function assertAddress(value: JsonValue | null | undefined): CorePgAddress {
  // asAddress now always returns a value, no need to check for null
  return asAddress(value);
}

export function assertLocationData(value: JsonValue | null | undefined): LocationData {
  // asLocationData now always returns a value, no need to check for null
  return asLocationData(value);
}

// TRPC Response Transformation Utilities for Frontend Compatibility
// These functions transform JsonValue fields to proper types before sending to frontend

/**
 * Transform an entity with address fields for TRPC response
 */
export function transformAddressFields<T extends Record<string, unknown>>(
  entity: T,
  addressFields: string[] = ['address', 'billingAddress']
): T {
  if (!entity) return entity;
  
  const transformed = { ...entity } as Record<string, unknown>;
  
  for (const field of addressFields) {
    if (field in transformed && transformed[field]) {
      const addressValue = transformed[field] as JsonValue;
      transformed[field] = asAddress(addressValue);
    }
  }
  
  return transformed as T;
}

/**
 * Transform an array of entities with address fields
 */
export function transformAddressFieldsArray<T extends Record<string, unknown>>(
  entities: T[],
  addressFields: string[] = ['address', 'billingAddress']
): T[] {
  if (!entities || !Array.isArray(entities)) return entities;
  
  return entities.map(entity => transformAddressFields(entity, addressFields));
}

/**
 * Transform content fields (JsonValue → proper content type)
 */
export function transformContentFields<T extends Record<string, unknown>>(
  entity: T,
  contentFields: string[] = ['content']
): T {
  if (!entity) return entity;
  
  const transformed = { ...entity } as Record<string, unknown>;
  
  for (const field of contentFields) {
    if (field in transformed && transformed[field]) {
      const contentValue = transformed[field] as JsonValue;
      // For now, pass through the content as-is since the frontend expects it
      // We can add specific transformations later if needed
      transformed[field] = asContentObject(contentValue);
    }
  }
  
  return transformed as T;
}

/**
 * Transform an array of entities with content fields
 */
export function transformContentFieldsArray<T extends Record<string, unknown>>(
  entities: T[],
  contentFields: string[] = ['content']
): T[] {
  if (!entities || !Array.isArray(entities)) return entities;
  
  return entities.map(entity => transformContentFields(entity, contentFields));
}

/**
 * Comprehensive transformation for entities with both address and content fields
 */
export function transformEntityFields<T extends Record<string, unknown>>(
  entity: T,
  options: {
    addressFields?: string[];
    contentFields?: string[];
  } = {}
): T {
  if (!entity) return entity;
  
  let transformed = entity;
  
  if (options.addressFields) {
    transformed = transformAddressFields(transformed, options.addressFields);
  }
  
  if (options.contentFields) {
    transformed = transformContentFields(transformed, options.contentFields);
  }
  
  return transformed;
}

/**
 * Transform an array of entities with both address and content fields
 */
export function transformEntityFieldsArray<T extends Record<string, unknown>>(
  entities: T[],
  options: {
    addressFields?: string[];
    contentFields?: string[];
  } = {}
): T[] {
  if (!entities || !Array.isArray(entities)) return entities;
  
  return entities.map(entity => transformEntityFields(entity, options));
}

/**
 * Special transformation for nested entities (e.g., customer with contacts)
 */
export function transformNestedEntityFields<T extends Record<string, unknown>>(
  entity: T,
  nestedTransformations: {
    [key: string]: {
      addressFields?: string[];
      contentFields?: string[];
    };
  } = {}
): T {
  if (!entity) return entity;
  
  let transformed = { ...entity } as Record<string, unknown>;
  
  // Transform root level fields
  transformed = transformEntityFields(transformed, {
    addressFields: ['address', 'billingAddress'],
    contentFields: ['content']
  }) as Record<string, unknown>;
  
  // Transform nested entity fields
  for (const [nestedField, options] of Object.entries(nestedTransformations)) {
    if (nestedField in transformed && transformed[nestedField]) {
      const nestedValue = transformed[nestedField];
      
      if (Array.isArray(nestedValue)) {
        transformed[nestedField] = transformEntityFieldsArray(nestedValue, options);
      } else if (nestedValue && typeof nestedValue === 'object') {
        transformed[nestedField] = transformEntityFields(nestedValue as Record<string, unknown>, options);
      }
    }
  }
  
  return transformed as T;
}

// ============================================================================
// TYPE ASSERTION UTILITIES FOR OPTION 3 - BACKWARDS COMPATIBILITY
// ============================================================================

/**
 * Type assertion utilities that cast JsonValue back to expected types
 * This restores backwards compatibility with minimal changes to existing code.
 */

// Type guard to validate address structure
function isValidAddressStructure(value: unknown): value is CorePgAddress {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  
  const obj = value as Record<string, unknown>;
  
  // Check required fields exist and are the correct type
  return (
    typeof obj.streetAddressPartOne === 'string' &&
    (typeof obj.streetAddressPartTwo === 'string' || obj.streetAddressPartTwo === null || obj.streetAddressPartTwo === undefined) &&
    typeof obj.city === 'string' &&
    typeof obj.state === 'string' &&
    typeof obj.postalCode === 'string'
  );
}

// Cast JsonValue to Address type with validation
export function castAsAddress(value: JsonValue | null | undefined): CorePgAddress {
  // If value is invalid, return a safe default address
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      streetAddressPartOne: '',
      streetAddressPartTwo: '',
      city: '',
      state: '',
      postalCode: '',
      county: '',
      latitude: 0,
      longitude: 0,
    };
  }
  
  // If structure is invalid, use the safer asAddress function to preserve partial data
  if (!isValidAddressStructure(value)) {
    console.warn('Invalid address structure detected in castAsAddress, preserving partial data', value);
    return asAddress(value);
  }
  
  return value as CorePgAddress;
}

// Type guard for TipTap content structure
function isValidTipTapContent(value: unknown): value is TipTapContent {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  
  const obj = value as Record<string, unknown>;
  return typeof obj.type === 'string' && Array.isArray(obj.content);
}

// Cast JsonValue to TipTap content with validation
export function castAsTipTapContent(value: JsonValue | null | undefined): TipTapContent {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return { type: 'doc', content: [] } as TipTapContent;
  }
  
  if (!isValidTipTapContent(value)) {
    console.warn('Invalid TipTap content structure, using default', value);
    return { type: 'doc', content: [] } as TipTapContent;
  }
  
  return value as TipTapContent;
}

// Type guard for DocumentNode structure
function isValidDocumentNode(value: unknown): value is DocumentNode {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  
  const obj = value as Record<string, unknown>;
  return typeof obj.kind === 'string' && 'definitions' in obj;
}

// Cast JsonValue to DocumentNode with validation
export function castAsDocumentNode(value: JsonValue | null | undefined): DocumentNode {
  // Return a default DocumentNode if invalid
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return {
      type: 'document',
      content: [],
    } as DocumentNode;
  }
  
  if (!isValidDocumentNode(value)) {
    console.warn('Invalid DocumentNode structure detected, using default', value);
    return {
      type: 'document',
      content: [],
    } as DocumentNode;
  }
  
  return value as DocumentNode;
}

// Type guard for route object
function isValidRoute(value: unknown): value is {name: string; id: string; dayOfWeek: number} {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  
  const obj = value as Record<string, unknown>;
  return (
    typeof obj.name === 'string' &&
    typeof obj.id === 'string' &&
    typeof obj.dayOfWeek === 'number'
  );
}

// Cast JsonValue to Routes array with validation
export function castAsRoutesArray(value: JsonValue | null | undefined): Array<{name: string; id: string; dayOfWeek: number}> {
  if (!Array.isArray(value)) {
    return [];
  }
  
  // Filter and validate each route
  const validRoutes = value.filter(isValidRoute);
  
  if (validRoutes.length !== value.length) {
    console.warn('Some routes were invalid and filtered out', value);
  }
  
  return validRoutes;
}

// Generic type cast for any expected object shape
// Note: This function cannot validate the generic type T at runtime
// Use specific validation functions when possible
export function castAsTypedObject<T>(value: JsonValue | null | undefined): T {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    // Return empty object cast to T as safe default
    return {} as T;
  }
  
  // Log warning about unsafe generic cast
  console.warn('Using unsafe generic cast - consider creating specific validation function for type');
  
  return value as T;
}

// Reverse conversion: PgAddress back to JsonValue (for legacy compatibility)
export function addressToJsonValue(address: CorePgAddress | null | undefined): JsonValue {
  if (!address) {
    return null;
  }
  return {
    streetAddressPartOne: address.streetAddressPartOne,
    streetAddressPartTwo: address.streetAddressPartTwo,
    city: address.city,
    state: address.state,
    postalCode: address.postalCode,
    county: address.county,
    latitude: address.latitude,
    longitude: address.longitude,
  } as JsonValue;
}
